import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { PermissionGate } from "@/modules/auth/presentation/ui/PermissionGate";
import { PerformanceMainDashboardWidget } from "@/modules/performance/presentation/ui/PerformanceMainDashboardWidget";
import {
  useAttendanceReportSummaryQuery,
  useAttendanceReportTrendQuery,
  usePackageSalesSummaryQuery,
  useRefundReportSummaryQuery,
  useRevenueSummaryQuery,
  useRevenueTrendQuery,
  useStudentReportLowRemainingQuery,
  useStudentReportSummaryQuery,
} from "@/modules/reports/presentation/api/queries";
import {
  DashboardChartEmptyState,
  DashboardQuickActions,
} from "@/modules/dashboard/presentation/ui/DashboardQuickActionsCard";
import {
  dashboardBentoGridClassName,
  dashboardKpiGridClassName,
  dashboardPageStackClassName,
  dashboardSectionCardClassName,
  dashboardSectionCardContentClassName,
  dashboardSectionCardFillClassName,
  dashboardSectionCardHeaderClassName,
} from "@/modules/dashboard/presentation/ui/dashboard-card-styles";
import { DashboardOverviewSection } from "@/modules/dashboard/presentation/ui/DashboardOverviewSection";
import { DashboardStatCard } from "@/modules/dashboard/presentation/ui/DashboardStatCard";
import { StudentReportLowRemainingTable } from "@/modules/reports/presentation/ui/StudentReportLowRemainingTable";
import { formatDate } from "@/shared/lib/date-time";
import { AreaChart, type AreaChartData } from "@/shared/ui/AreaChart";
import { BarChart, type BarChartData } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { PieChart, type PieChartData } from "@/shared/ui/PieChart";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { endOfMonth, format, startOfMonth } from "date-fns";
import {
  BanknoteIcon,
  ClipboardListIcon,
  GraduationCapIcon,
  PackageIcon,
  Undo2Icon,
} from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";

export type DashboardSearch = {
  year?: number;
  month?: number;
};

/** ຊື່ເດືອນ (ລາວ) — ບໍ່ພຶ່ງ locale ຂອງ runtime */
const LAO_MONTH_NAMES = [
  "ມັງກອນ",
  "ກຸມພາ",
  "ມີນາ",
  "ເມສາ",
  "ພຶດສະພາ",
  "ມິຖຸນາ",
  "ກໍລະກົດ",
  "ສິງຫາ",
  "ກັນຍາ",
  "ຕຸລາ",
  "ພະຈິກ",
  "ທັນວາ",
] as const;

const MONTH_SELECT_OPTIONS = LAO_MONTH_NAMES.map((label, i) => ({
  value: String(i + 1),
  label,
}));

/** ຊ່ວງວັນທີ່ເດືອນປະຕິທິນ: ວັນທີ 1 — ວັນສຸດທ້າຍເດືອນ (ເດືອນທີ່ຍັງບໍ່ມາຮອດ ຈຳກັດເປັນມື້ນີ້) */
function dashboardCalendarMonthRange(
  year: number,
  month: number,
): { dateFrom: string; dateTo: string } {
  const start = startOfMonth(new Date(year, month - 1, 1));
  const end = endOfMonth(new Date(year, month - 1, 1));
  const startStr = format(start, "yyyy-MM-dd");
  const endStr = format(end, "yyyy-MM-dd");
  const todayStr = format(new Date(), "yyyy-MM-dd");
  if (startStr > todayStr) {
    return { dateFrom: todayStr, dateTo: todayStr };
  }
  return { dateFrom: startStr, dateTo: endStr };
}

function defaultMonthForYear(year: number): number {
  const now = new Date();
  if (year === now.getFullYear()) return now.getMonth() + 1;
  return 1;
}

function useDashboardQuery(search: DashboardSearch) {
  const hasCalendarRange =
    search.year != null &&
    search.month != null &&
    search.month >= 1 &&
    search.month <= 12;

  const { dateFrom, dateTo } = useMemo(() => {
    if (hasCalendarRange && search.year != null && search.month != null) {
      return dashboardCalendarMonthRange(search.year, search.month);
    }
    const now = new Date();
    return dashboardCalendarMonthRange(now.getFullYear(), now.getMonth() + 1);
  }, [hasCalendarRange, search.year, search.month]);

  const baseQuery = useMemo(() => ({ dateFrom, dateTo }), [dateFrom, dateTo]);

  const revenueSummaryQuery = useMemo(() => baseQuery, [baseQuery]);
  const revenueTrendQuery = useMemo(
    () => ({ ...baseQuery, groupBy: "day" as const }),
    [baseQuery],
  );
  const attendanceSummaryQuery = useMemo(() => baseQuery, [baseQuery]);
  const attendanceTrendQuery = useMemo(
    () => ({ ...baseQuery, groupBy: "day" as const }),
    [baseQuery],
  );
  const studentSummaryQuery = useMemo(() => baseQuery, [baseQuery]);
  const packageSalesSummaryQuery = useMemo(() => baseQuery, [baseQuery]);
  const lowRemainingQuery = useMemo(
    () => ({ thresholdSessions: 3, thresholdHours: 2, limit: 10 }),
    [],
  );

  return {
    dateFrom,
    dateTo,
    revenueSummaryQuery,
    revenueTrendQuery,
    attendanceSummaryQuery,
    attendanceTrendQuery,
    studentSummaryQuery,
    packageSalesSummaryQuery,
    lowRemainingQuery,
  };
}

export function DashboardPage() {
  const nav = useNavigate({ from: "/app/dashboard" });
  const search = useSearch({ from: "/app/dashboard" }) as DashboardSearch;
  const {
    dateFrom,
    dateTo,
    revenueSummaryQuery,
    revenueTrendQuery,
    attendanceSummaryQuery,
    attendanceTrendQuery,
    studentSummaryQuery,
    packageSalesSummaryQuery,
    lowRemainingQuery,
  } = useDashboardQuery(search);

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => currentYear - i).filter(
        (y) => y >= 2000,
      ),
    [currentYear],
  );

  const { data: revenueSummary, isLoading: revenueLoading } =
    useRevenueSummaryQuery(revenueSummaryQuery);
  const { data: revenueTrend = [] } = useRevenueTrendQuery(revenueTrendQuery);
  const { data: attendanceSummary, isLoading: attendanceLoading } =
    useAttendanceReportSummaryQuery(attendanceSummaryQuery);
  const { data: attendanceTrend = [] } =
    useAttendanceReportTrendQuery(attendanceTrendQuery);
  const { data: studentSummary, isLoading: studentLoading } =
    useStudentReportSummaryQuery(studentSummaryQuery);
  const { data: packageSalesSummary, isLoading: packageSalesLoading } =
    usePackageSalesSummaryQuery(packageSalesSummaryQuery);
  const { data: refundSummary, isLoading: refundLoading } =
    useRefundReportSummaryQuery({ filters: [] });
  const { data: lowRemaining = [], isLoading: lowRemainingLoading } =
    useStudentReportLowRemainingQuery(lowRemainingQuery);

  const yearSelectValue = String(search.year ?? currentYear);

  const onYearSelect = useCallback(
    (v: string) => {
      const y = Number(v);
      const nextMonth =
        search.month ?? (Number.isFinite(y) ? defaultMonthForYear(y) : 1);
      nav({
        search: {
          ...search,
          year: y,
          month: nextMonth,
        },
      });
    },
    [nav, search],
  );

  const onMonthSelect = useCallback(
    (v: string) => {
      if (search.year == null) return;
      nav({
        search: {
          ...search,
          month: Number(v),
        },
      });
    },
    [nav, search],
  );

  useEffect(() => {
    if (search.year != null && search.month == null) {
      nav({
        replace: true,
        search: {
          ...search,
          month: defaultMonthForYear(search.year),
        },
      });
    }
  }, [search, nav]);

  const revenueAreaData: AreaChartData[] = useMemo(
    () =>
      revenueTrend.map((t) => ({
        period: formatDate(t.period),
        totalRevenue: t.totalRevenue,
        packageRevenue: t.packageRevenue,
        privateLessonRevenue: t.privateLessonRevenue,
        roomBookingRevenue: t.roomBookingRevenue,
      })),
    [revenueTrend],
  );

  const revenueAreaConfig: ChartConfig = useMemo(
    () => ({
      period: { label: "ວັນທີ" },
      totalRevenue: { label: "ລາຍຮັບທັງໝົດ", color: "var(--chart-1)" },
      packageRevenue: { label: "ແພັກເກດ", color: "var(--chart-2)" },
      privateLessonRevenue: { label: "ຮຽນສ່ວນຕົວ", color: "var(--chart-3)" },
      roomBookingRevenue: { label: "ຄ່າເຊົ່າຫ້ອງ", color: "var(--chart-4)" },
    }),
    [],
  );

  const revenuePieData: PieChartData[] = useMemo(() => {
    if (!revenueSummary) return [];
    const total = revenueSummary.totalRevenue || 1;
    return [
      {
        name: "ແພັກເກດ",
        value: revenueSummary.packageRevenue,
        pct: ((revenueSummary.packageRevenue / total) * 100).toFixed(1),
      },
      {
        name: "ຮຽນສ່ວນຕົວ",
        value: revenueSummary.privateLessonRevenue,
        pct: ((revenueSummary.privateLessonRevenue / total) * 100).toFixed(1),
      },
      {
        name: "ຄ່າເຊົ່າຫ້ອງ",
        value: revenueSummary.roomBookingRevenue,
        pct: ((revenueSummary.roomBookingRevenue / total) * 100).toFixed(1),
      },
    ].filter((x) => x.value > 0);
  }, [revenueSummary]);

  const revenuePieConfig: ChartConfig = useMemo(
    () => ({
      value: { label: "ລາຍຮັບ" },
      ແພັກເກດ: { label: "ແພັກເກດ", color: "var(--chart-1)" },
      ຮຽນສ່ວນຕົວ: {
        label: "ຮຽນສ່ວນຕົວ",
        color: "var(--chart-2)",
      },
      ຄ່າເຊົ່າຫ້ອງ: {
        label: "ຄ່າເຊົ່າຫ້ອງ",
        color: "var(--chart-3)",
      },
    }),
    [],
  );

  const attendanceBarData: BarChartData[] = useMemo(
    () =>
      attendanceTrend.map((t) => ({
        period: formatDate(t.date),
        totalAttendance: t.totalAttendance,
        uniqueStudents: t.uniqueStudents,
      })),
    [attendanceTrend],
  );

  const attendanceBarConfig: ChartConfig = useMemo(
    () => ({
      period: { label: "ວັນທີ" },
      totalAttendance: { label: "ການເຂົ້າຮຽນ", color: "var(--chart-1)" },
      uniqueStudents: { label: "ນັກຮຽນໃນວັນ", color: "var(--chart-2)" },
    }),
    [],
  );

  const goToReport = useCallback(
    (to: string) => {
      void nav({
        to,
        search: { dateFrom, dateTo },
      });
    },
    [nav, dateFrom, dateTo],
  );

  return (
    <>
      <Header>
        <Breadcrumb items={[{ label: "ແຜງຄວບຄຸມ" }]} />
      </Header>

      <Main>
        <div className={dashboardPageStackClassName}>
          <DashboardOverviewSection
            yearOptions={yearOptions}
            yearSelectValue={yearSelectValue}
            monthValue={
              search.month != null ? String(search.month) : undefined
            }
            monthOptions={MONTH_SELECT_OPTIONS}
            monthDisabled={search.year == null}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onYearSelect={onYearSelect}
            onMonthSelect={onMonthSelect}
          />

          <DashboardQuickActions
            pendingRefundCount={refundSummary?.requestedCount}
          />

          <PermissionGate all={["performance-reports:read"]}>
            <PerformanceMainDashboardWidget dateFrom={dateFrom} dateTo={dateTo} />
          </PermissionGate>

          <div className={dashboardKpiGridClassName}>
              <PermissionGate all={["reports:read"]}>
                <DashboardStatCard
                  title="ລາຍຮັບທັງໝົດ"
                  tone="revenue"
                  icon={BanknoteIcon}
                  isLoading={revenueLoading}
                  onClick={() => goToReport("/app/reports/revenue")}
                  value={
                    <CompactMoneyAmount
                      amount={revenueSummary?.totalRevenue ?? 0}
                    />
                  }
                />
              </PermissionGate>

              <PermissionGate all={["reports:read"]}>
                <DashboardStatCard
                  title="ການເຂົ້າຮຽນ"
                  tone="attendance"
                  icon={ClipboardListIcon}
                  isLoading={attendanceLoading}
                  onClick={() => goToReport("/app/reports/attendance")}
                  value={(
                    attendanceSummary?.totalAttendance ?? 0
                  ).toLocaleString()}
                />
              </PermissionGate>

              <PermissionGate all={["reports:read"]}>
                <DashboardStatCard
                  title="ນັກຮຽນໃໝ່"
                  tone="students"
                  icon={GraduationCapIcon}
                  isLoading={studentLoading}
                  onClick={() => goToReport("/app/reports/students")}
                  value={(
                    studentSummary?.newStudentsCount ?? 0
                  ).toLocaleString()}
                />
              </PermissionGate>

              <PermissionGate all={["reports:read"]}>
                <DashboardStatCard
                  title="ຂາຍແພັກເກດ"
                  tone="packages"
                  icon={PackageIcon}
                  isLoading={packageSalesLoading}
                  onClick={() => goToReport("/app/reports/package-sales")}
                  value={(
                    packageSalesSummary?.totalSalesCount ?? 0
                  ).toLocaleString()}
                />
              </PermissionGate>

              <PermissionGate all={["refunds:read"]}>
                <DashboardStatCard
                  title="ຄຳຂໍຄືນເງິນລໍຖ້າ"
                  tone="refunds"
                  icon={Undo2Icon}
                  isLoading={refundLoading}
                  onClick={() =>
                    void nav({
                      to: "/app/refunds",
                      search: {
                        filters: [
                          {
                            field: "status",
                            op: "eq",
                            value: "requested",
                          },
                        ],
                      } as never,
                    })
                  }
                  value={(refundSummary?.requestedCount ?? 0).toLocaleString()}
                />
              </PermissionGate>
          </div>

          <div className={dashboardBentoGridClassName}>
              <PermissionGate all={["reports:read"]}>
                <Card
                  className={cn(
                    dashboardSectionCardClassName,
                    dashboardSectionCardFillClassName,
                    "lg:col-span-7",
                  )}
                >
                  <CardHeader className={dashboardSectionCardHeaderClassName}>
                    <CardTitle>ແນວໂນ້ມລາຍຮັບ</CardTitle>
                  </CardHeader>
                  <CardContent className={dashboardSectionCardContentClassName}>
                    {revenueAreaData.length > 0 ? (
                      <AreaChart
                        data={revenueAreaData}
                        config={revenueAreaConfig}
                        xDataKey="period"
                        valueDataKey={[
                          "packageRevenue",
                          "privateLessonRevenue",
                          "roomBookingRevenue",
                        ]}
                        xAxisTickFormatter={(v) => v.slice(0, 5)}
                        stacked
                        className="aspect-video max-h-[260px]"
                      />
                    ) : (
                      <DashboardChartEmptyState className="aspect-video max-h-[260px]" />
                    )}
                  </CardContent>
                </Card>
              </PermissionGate>

              <PermissionGate all={["reports:read"]}>
                <Card
                  className={cn(
                    dashboardSectionCardClassName,
                    dashboardSectionCardFillClassName,
                    "lg:col-span-5",
                  )}
                >
                  <CardHeader className={dashboardSectionCardHeaderClassName}>
                    <CardTitle>ສັດສ່ວນລາຍຮັບ</CardTitle>
                  </CardHeader>
                  <CardContent className={dashboardSectionCardContentClassName}>
                    {revenuePieData.length > 0 ? (
                      <PieChart
                        data={revenuePieData}
                        config={revenuePieConfig}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={80}
                        showTooltip
                        hideTooltipLabel={false}
                        className="mx-auto aspect-square max-h-[240px]"
                      />
                    ) : (
                      <DashboardChartEmptyState className="mx-auto aspect-square max-h-[240px]" />
                    )}
                  </CardContent>
                </Card>
              </PermissionGate>

              <PermissionGate all={["reports:read"]}>
                <Card
                  className={cn(
                    dashboardSectionCardClassName,
                    dashboardSectionCardFillClassName,
                    "lg:col-span-7",
                  )}
                >
                  <CardHeader className={dashboardSectionCardHeaderClassName}>
                    <CardTitle>ແນວໂນ້ມການເຂົ້າຮຽນ</CardTitle>
                  </CardHeader>
                  <CardContent className={dashboardSectionCardContentClassName}>
                    {attendanceBarData.length > 0 ? (
                      <BarChart
                        data={attendanceBarData}
                        config={attendanceBarConfig}
                        xDataKey="period"
                        valueDataKey={["totalAttendance"]}
                        xAxisTickFormatter={(v) => v.slice(0, 5)}
                        barRadius={6}
                        className="aspect-video max-h-[260px]"
                      />
                    ) : (
                      <DashboardChartEmptyState className="aspect-video max-h-[260px]" />
                    )}
                  </CardContent>
                </Card>
              </PermissionGate>

              <PermissionGate all={["reports:read"]}>
                <Card
                  className={cn(
                    dashboardSectionCardClassName,
                    dashboardSectionCardFillClassName,
                    "lg:col-span-5",
                  )}
                >
                  <CardHeader
                    className={cn(
                      dashboardSectionCardHeaderClassName,
                      "flex flex-row flex-wrap items-center justify-between gap-2 space-y-0",
                    )}
                  >
                    <CardTitle>ນັກຮຽນແພັກເກດເຫຼືອນ້ອຍ</CardTitle>
                    <div className="flex shrink-0 items-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => void nav({ to: "/app/reports/students" })}
                      >
                        ເບິ່ງທັງໝົດ
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className={dashboardSectionCardContentClassName}>
                    <StudentReportLowRemainingTable
                      variant="list"
                      isLoading={lowRemainingLoading}
                      data={lowRemaining}
                    />
                  </CardContent>
                </Card>
              </PermissionGate>
          </div>
        </div>
      </Main>
    </>
  );
}
